import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { kebabCase } from 'lodash-es';

import Icon from '@klv/atoms/icon';
import { generateClassName } from '@klv/shared/utils/functions';
import { getDismissed, handleDismiss, stripTags } from './utils';

import * as styles from './styles/alertBar.module.css';

import type { IAlertBar } from './types';


export const AlertBar = ({
  backgroundColor = 'lemon',
  backgroundImage,
  className = '',
  href = '',
  icon,
  message = '',
  openInNewTab = false,
  scrollDirection,
  textColor = 'charcoal',
  ...props
}: IAlertBar) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(true);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    const isDismissed = getDismissed();
    setDismissed(isDismissed);

    if (!isDismissed) {
      setTimeout(() => {
        setReveal(true);
      }, 900);
    }
  }, []);

  const Content = href ? 'a' : 'div';

  if (dismissed) return null;

  return (
    <div
      className={generateClassName([
        className,
        styles.alertBar,
        reveal && styles.reveal,
        scrollDirection === 'down' && styles.scrolled,
      ])}
      style={{
        '--color-alert-bar-background': `var(--color-${kebabCase(backgroundColor)})`,
        '--color-alert-bar-text': `var(--color-${kebabCase(textColor)})`,
        ...(backgroundImage && { '--image-alert-bar-background': `url(\'${backgroundImage}\')` }),
      } as React.CSSProperties}
      {...props}
    >
      <div className={generateClassName([styles.grid, 'grid'])}>
        <Content
          className={styles.content}
          href={href}
          target={href && openInNewTab ? '_blank' : undefined}
        >
          {icon && (
            <Icon
              aria-hidden='true'
              className={styles.icon}
              color='currentColor'
              iconName={icon}
              size={1.2}
            />
          )}
          <div
            className={styles.message}
            dangerouslySetInnerHTML={{ __html: stripTags(message) }}
          />
          <Icon
            aria-hidden='true'
            className={styles.chevron}
            color='currentColor'
            iconName='ChevronDown'
            size={0.5}
          />
        </Content>
        <button
          aria-label={t('alertBar_dismiss')}
          className={styles.dismiss}
          onClick={() => handleDismiss(setDismissed)}
        >
          <Icon
            color='currentColor'
            iconName='X'
            size={1}
          />
        </button>
      </div>
    </div>
  );
};
